import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import StatusChip from "../components/StatusChip";
import { useTheme } from "@mui/styles";
import Loader from "../../../components/Loader/Loader";
import {
  deleteSubmission,
  fetchBrainSubmissionsData,
} from "../../../services/api";
import APP_CONSTANTS from "../../../utils/config";

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { brainStyles } from "../../../theme/style";
import { useDispatch } from "react-redux";
import { formatDateToString } from "../../../utils/utils";
import useApi from "../../../hooks/useApi";
import Error from "../../../components/Error/Error";
import { updateAlertInfo } from "../../../redux/slice/userSlice";
import { useNavigate } from "react-router-dom";

export default function Reviewer() {
  const [batches, setBatches] = useState([]);
  const dispatch = useDispatch();

  const navigate = useNavigate();
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const {
    status: brainSubmissionStatus,
    errorMessage: brainSubmissionError,
    callApi: fetchBrainLandingPageData,
  } = useApi(fetchBrainSubmissionsData);
  const theme = useTheme();
  const classes = brainStyles(theme);

  const loadData = async () => {
    const response = await fetchBrainLandingPageData();
    if (response.success) {
      setBatches(response.data);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteSubmission({ submission_id: deleteTarget.id });
      dispatch(
        updateAlertInfo({
          message: "Submission deleted successfully",
          open: true,
          severity: "success",
        })
      );
      setDeleteTarget(null);
      await loadData();
    } catch (e) {
      dispatch(
        updateAlertInfo({
          message: "Failed to delete submission, please try again later!",
          open: true,
          severity: "error",
        })
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: 3,
      }}
    >
      {/* Header Bar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography
            variant="h1"
            sx={{ fontSize: "1.25rem", color: theme.palette.secondary.main2 }}
          >
            My Submissions
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.primary.text2 }}
          >
            Track status of your submitted edits
          </Typography>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          border: `1px solid ${theme.palette.brain.mis.grey.border1}`,
          backgroundColor: theme.palette.brain.table.tableBackground,
          borderRadius: 2,
          "&::-webkit-scrollbar": { height: 6, width: 6 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: theme.palette.brain.mis.grey.background1,
            borderRadius: 3,
          },
        }}
      >
        {brainSubmissionStatus === APP_CONSTANTS.LOADING ||
        brainSubmissionStatus === APP_CONSTANTS.INITIAL ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
              width: "100%",
              height: "100%",
            }}
          >
            <Loader
              message={"Loading data..."}
              showLoadingImage={false}
              top={"50%"}
              circleStyle={{ color: theme.palette.secondary.main2 }}
            />
          </Box>
        ) : brainSubmissionStatus === APP_CONSTANTS.SUCCESS ? (
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHeader}>
                  Submission ID
                </TableCell>
                <TableCell className={classes.tableHeader}>Rows</TableCell>
                <TableCell className={classes.tableHeader}>Changes</TableCell>
                <TableCell className={classes.tableHeader}>
                  Submitted at
                </TableCell>
                <TableCell className={classes.tableHeader}>
                  Assigned Approver
                </TableCell>
                <TableCell className={classes.tableHeader}>Status</TableCell>
                <TableCell className={classes.tableHeader} align="center">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {batches.map((batch, idx) => {
                return (
                  <TableRow
                    key={batch.id}
                    hover
                    sx={{
                      cursor: "pointer",
                      backgroundColor:
                        idx % 2 === 0
                          ? "transparent"
                          : theme.palette.brain.table.row.background,
                      "&:hover": {
                        backgroundColor:
                          theme.palette.brain.table.row.hoverBackground,
                      },
                      transition: "background-color 0.12s ease",
                    }}
                  >
                    <TableCell
                      sx={{
                        color: theme.palette.brain.table.row.text,
                      }}
                    >
                      #{batch.id}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: theme.palette.brain.table.row.text,
                      }}
                    >
                      {batch.total_rows}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: theme.palette.brain.table.row.text,
                      }}
                    >
                      {batch.total_changes}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: theme.palette.brain.table.row.text,
                      }}
                    >
                      {formatDateToString(batch.submittedAt, "DD-MMM-YYYY")}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: theme.palette.brain.table.row.text,
                      }}
                    >
                      {batch.assigned_approver ? batch.assigned_approver : "_"}
                    </TableCell>
                    <TableCell>
                      <StatusChip status={batch.status} />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: "inline-flex", gap: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<OpenInNewIcon />}
                          onClick={() =>
                            navigate(`/brain/submissions/${batch.id}`)
                          }
                          sx={{
                            textTransform: "none",
                            fontSize: "0.75rem",
                            borderColor: theme.palette.secondary.main2,
                            color: theme.palette.secondary.main2,

                            "& .MuiButton-startIcon svg": {
                              fontSize: 16,
                            },

                            "&:hover": {
                              borderColor: theme.palette.secondary.main2,
                              color: theme.palette.secondary.main2,
                            },
                          }}
                        >
                          Open
                        </Button>
                        <Tooltip
                          title={
                            ["pending"].includes(batch.status)
                              ? "Delete submission"
                              : "Only pending submissions can be deleted"
                          }
                        >
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              disabled={!["pending"].includes(batch.status)}
                              startIcon={
                                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                              }
                              onClick={(e) => {
                                setDeleteTarget(batch);
                                e.stopPropagation();
                              }}
                              sx={{
                                textTransform: "none",
                                fontSize: "0.75rem",
                                fontWeight: 600,
                              }}
                            >
                              Delete
                            </Button>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
              {batches.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    align="center"
                    sx={{ py: 4, color: theme.palette.brain.table.row.text }}
                  >
                    No submissions yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Error message={brainSubmissionError} />
          </Box>
        )}
      </TableContainer>

      <Dialog
        open={!!deleteTarget}
        onClose={() => !deleting && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
            background: theme.palette.brain.table.tableBackground,
          },
        }}
      >
        <DialogTitle
          sx={{
            color: theme.palette.secondary.main2,
          }}
        >
          Delete Submission
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.brain.table.row.text }}
          >
            Are you sure you want to delete submission{" "}
            <strong style={{ color: theme.palette.secondary.main2 }}>
              #{deleteTarget?.id}
            </strong>
            ? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setDeleteTarget(null)}
            disabled={deleting}
            sx={{
              textTransform: "none",
              fontSize: "0.75rem",
              borderColor: theme.palette.secondary.main2,
              color: theme.palette.secondary.main2,
              "&:hover": {
                borderColor: theme.palette.secondary.main2,
                color: theme.palette.secondary.main2,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDelete}
            disabled={deleting}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
